import {inject, Injectable, OnDestroy} from '@angular/core';
import {defer, from, map, merge, mergeWith, Observable, of, ReplaySubject, share, Subscription, switchMap} from 'rxjs';
import {ObjectData, ObjektDataService} from './objekt-data.service';
import {SwissSettingsService} from './swiss-settings.service';
import Fuse, {Expression, IFuseOptions} from 'fuse.js';
import {TreeviewService} from './treeview.service';
import {SearchFilterOptions, SearchParameters} from './objekt-search.service';
import {HelperService} from './helper.service';

@Injectable({
    providedIn: 'root',
})
export class OfflineObjektSearchService implements OnDestroy {
    private readonly swissSettings = inject(SwissSettingsService);
    private readonly treeviewCacheService = inject(TreeviewService);
    private readonly objektDataService = inject(ObjektDataService);

    private readonly fuseOptions: IFuseOptions<any> = {
        keys: [
            {name: 'Name', getFn: (obj) => obj.name},
            {name: 'Description', getFn: (obj) => obj.description},
            {name: 'ObjectNumber', getFn: (obj) => obj.objectNr},
            {name: 'PL', getFn: (obj) => obj.projectLeader},
            {name: 'Note4', getFn: (obj) => obj.note4},
            {name: 'Status', getFn: (obj) => obj.status},
            {name: 'Town', getFn: (obj) => obj.town},
        ],
        includeMatches: true,
        threshold: 0,
        isCaseSensitive: false,
        shouldSort: true,
        ignoreLocation: true,
        useExtendedSearch: true,
        includeScore: true,
    };

    private searcher = new Fuse<ObjectData>([], this.fuseOptions);

    private readonly data$;

    private readonly subscriptions = new Subscription();

    constructor(private swissSettingsService: SwissSettingsService, private helperService: HelperService) {
        this.data$ = this.swissSettingsService.getConfigsLoaded$().pipe(mergeWith(this.helperService.getOnlineStateChanged())).pipe(
            switchMap((data) => {
                return this.getData(this.swissSettings.getObjViewConfig());
            }),
            share({connector: () => new ReplaySubject(1)})
        );
        this.subscriptions.add(this.data$.subscribe((data) => this.searcher.setCollection(data)));
    }

    public ngOnDestroy(): void {
        this.searcher.setCollection([]);
        this.subscriptions.unsubscribe();
    }

    public getFilterValues(): SearchFilterOptions {
        const filterValues = this.objektDataService.getObjektSucheDaten().pipe(share({connector: () => new ReplaySubject(1)}));

        return {
            placesFilterOptions: filterValues.pipe(map((data) => data.objektOrte)),
            projectLeaderFilterOptions: filterValues.pipe(map((data) => data.projektleiter)),
            statusFilterOptions: filterValues.pipe(map((data) => data.objektStatus)),
        };
    }

    public search(searchParams: SearchParameters): Observable<ObjectData[]> {
        let searchParameters = this.buildSearchParameters(searchParams);
        let searchResults = this.searcher.search(searchParameters, {limit: 500});

        return of(searchResults.flatMap((searchResult) => searchResult.item));
    }

    private buildSearchParameters({searchTerm, note4, place, projectLeader, objStatus}: SearchParameters): Expression {
        const searchTerms = [
            !searchTerm ? undefined : {$or: [{Name: searchTerm.toLowerCase()}, {ObjectNumber: searchTerm.toLowerCase()}, {Description: searchTerm.toLowerCase()}]},
            !note4 ? undefined : {Note4: note4.toLowerCase()},
            (!place || !place.length) ? undefined : {$or: place.map((p) => ({Town: p.town}))},
            (!projectLeader || !projectLeader.length) ? undefined : {$or: projectLeader.map((pl) => ({PL: pl.id.toString()}))},
            (!objStatus || !objStatus.length) ? undefined : {$or: objStatus.map((status) => ({Status: status.id.toString()}))},
        ] satisfies (Expression | undefined)[];

        return {$and: searchTerms.filter((t) => !!t).map((t) => t as Expression)} satisfies Expression;
    }

    private getData(objViewConfig: number): Observable<ObjectData[]> {
        //legacy online search for older object Trees
        if (this.helperService.isWebServiceAvailable()) {
            switch (objViewConfig) {
                case 2:
                case 3:
                case 5:
                case 6:
                    return merge(
                        defer(() => of((this.treeviewCacheService.getObjectListData() as ObjectData[]) || [])),
                        this.treeviewCacheService.getObjectListDataChanged() as Observable<ObjectData[]>
                    );
                default:
                    return merge(
                        defer(() => of(this.treeviewCacheService.getData() || [])),
                        this.treeviewCacheService.getTreeViewDataChanged()
                    );
            }
        } else {
            return merge(
                defer(() => from((this.objektDataService.getOfflineObjektBaum()) || [])),
                //?offline data cant change?
                new Observable() as Observable<ObjectData[]>
            );
        }
    }
}